import { initReactI18next } from 'react-i18next';

import i18next, { Resource } from 'i18next';
import detector from 'i18next-browser-languagedetector';
import { mergeDeepLeft } from 'ramda';

import normalizeChangeLanguage from '../normalizeChangeLanguage';
import normalizeLanguage from '../normalizeLanguage';
import { InitI18nOptions } from './initI18n.types';
import lazyBackend from './lazyBackend';

export default function initI18n(
  resources: Resource,
  options: Partial<InitI18nOptions> = {},
  useDetector = false,
) {
  const langs = Object.keys(resources) as string[];

  const opts: InitI18nOptions = {
    lng: langs[0],
    fallbackLng: langs[0],
    supportedLngs: langs,
    defaultNS: 'translation',
    debug: process.env['DEBUG'] === 'true',
    interpolation: undefined,
  };

  const {
    lng,
    fallbackLng,
    supportedLngs,
    defaultNS,
    debug,
    interpolation,
    lazyBackend: useLazyBackend,
  } = mergeDeepLeft(options, opts) as InitI18nOptions;

  // Before `init`, which calls `changeLanguage` for the initial language too.
  normalizeChangeLanguage(i18next);

  if (useDetector) {
    const chain = useLazyBackend
      ? i18next.use(lazyBackend).use(detector).use(initReactI18next)
      : i18next.use(detector).use(initReactI18next);

    chain.init({
      lng: undefined,
      appendNamespaceToCIMode: true,
      fallbackLng,
      supportedLngs,
      debug,
      defaultNS,
      resources,
      partialBundledLanguages: true,
      nsSeparator: false,
      interpolation,

      detection: {
        order: [
          'querystring',
          'cookie',
          // 'localStorage',
          'sessionStorage',
          'navigator',
          'htmlTag',
        ],
        lookupQuerystring: 'lng',
        lookupCookie: 'i18next',
        lookupLocalStorage: 'i18nextLng',
        lookupSessionStorage: 'i18nextLng',
        // cache user language
        caches: [],
        excludeCacheFor: ['cimode'],
        convertDetectedLanguage: (lng?: string) =>
          lng ? normalizeLanguage(lng) : 'de',
      },
    });
    return;
  }

  const chain = useLazyBackend
    ? i18next.use(lazyBackend).use(initReactI18next)
    : i18next.use(initReactI18next);

  chain.init({
    lng,
    fallbackLng,
    supportedLngs,
    debug,
    defaultNS,
    resources,
    partialBundledLanguages: true,
    nsSeparator: false,
    interpolation,
  });
}
